import { CampusIQClient } from '../client';
import { CampusIQError } from '../error';

export abstract class BaseResource {
  protected client: CampusIQClient;

  constructor(client: CampusIQClient) {
    this.client = client;
  }

  protected async requestGet<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    try {
      const url = new URL(`${this.client.baseURL}${endpoint}`);

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(v => url.searchParams.append(key, String(v)));
            } else {
              url.searchParams.append(key, String(value));
            }
          }
        });
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.client.apiKey}`,
          'X-School-ID': this.client.schoolId,
          'Content-Type': 'application/json',
          'User-Agent': `CampusIQ-SDK/${this.client.version}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new CampusIQError(`HTTP ${response.status}: ${response.statusText}`, response.status, errorText);
      }

      return await response.json();
    } catch (error: any) {
      if (error instanceof CampusIQError) throw error;
      throw new CampusIQError(`Request failed: ${error.message}`, 0, error.message);
    }
  }

  protected async requestPost<T>(endpoint: string, data?: any): Promise<T> {
    return this.sendRequest<T>('POST', endpoint, data);
  }

  protected async requestPut<T>(endpoint: string, data?: any): Promise<T> {
    return this.sendRequest<T>('PUT', endpoint, data);
  }

  protected async requestPatch<T>(endpoint: string, data?: any): Promise<T> {
    return this.sendRequest<T>('PATCH', endpoint, data);
  }

  protected async requestDelete<T>(endpoint: string): Promise<T> {
    return this.sendRequest<T>('DELETE', endpoint);
  }

  private async sendRequest<T>(method: string, endpoint: string, data?: any): Promise<T> {
    try {
      const response = await fetch(`${this.client.baseURL}${endpoint}`, {
        method,
        headers: {
          'Authorization': `Bearer ${this.client.apiKey}`,
          'X-School-ID': this.client.schoolId,
          'Content-Type': 'application/json',
          'User-Agent': `CampusIQ-SDK/${this.client.version}`,
        },
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new CampusIQError(`HTTP ${response.status}: ${response.statusText}`, response.status, errorText);
      }

      return await response.json();
    } catch (error: any) {
      if (error instanceof CampusIQError) throw error;
      throw new CampusIQError(`Request failed: ${error.message}`, 0, error.message);
    }
  }
}
